/**
 * Warehouse contact management with soft-delete. List, detail, create, update,
 * and delete plus restore of a soft-deleted contact and its order/usage
 * history.
 *
 * Contains:
 * - fetchContactsAPI / getContactAPI: list and detail reads
 * - createContactAPI / updateContactAPI / deleteContactAPI: contact writes
 * - restoreContactAPI: undelete a soft-deleted contact
 * - fetchUsageHistoryAPI: a contact's usage history
 */
import apiClient from '@sophocles/http-common';

export const CONTACTS_ENDPOINT = '/warehouse/contacts';

/**
 * Fetch contacts from backend API with filters
 *
 * @param {Object} params - All query parameters to pass to backend
 * @returns {Promise<Object>} Response with results array and pagination info
 */
export const fetchContactsAPI = async (params = {}) => {
  const response = await apiClient.get(`${CONTACTS_ENDPOINT}/`, { params });
  return response.data;
};

/**
 * Fetch a single contact by ID
 */
export const getContactAPI = async contactId => {
  const response = await apiClient.get(`${CONTACTS_ENDPOINT}/${contactId}/`);
  return response.data;
};

/**
 * Update contact (including marking as primary via is_primary_rma field)
 */
export const updateContactAPI = async (contactId, data) => {
  const response = await apiClient.patch(`${CONTACTS_ENDPOINT}/${contactId}/`, data);
  return response.data;
};

/**
 * Delete contact (soft delete by default)
 */
export const deleteContactAPI = async contactId => {
  await apiClient.delete(`${CONTACTS_ENDPOINT}/${contactId}/`);
};

/**
 * Create new contact
 */
export const createContactAPI = async data => {
  const response = await apiClient.post(`${CONTACTS_ENDPOINT}/`, data);
  return response.data;
};

/**
 * Restore soft-deleted contact (staff only)
 */
export const restoreContactAPI = async contactId => {
  const response = await apiClient.post(`${CONTACTS_ENDPOINT}/${contactId}/restore/`);
  return response.data;
};

/**
 * Fetch usage history for a contact (RMAs and Order Sales)
 *
 * @param {number} contactId - Contact ID
 * @returns {Promise<Object>} Response with rmas, order_sales arrays and total_count
 */
export const fetchUsageHistoryAPI = async contactId => {
  const response = await apiClient.get(`${CONTACTS_ENDPOINT}/${contactId}/usage_history/`);
  return response.data;
};
